import { LosseLink, useLoaderData } from '@ubo/losse-sjedel'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Fragment } from 'react'
import Animated from '~/components/elements/Animated'
import Content from '~/components/elements/Content'
import GreenGreenGrass from '~/components/elements/GreenGreenGrass'
import type { Page_Flexcontent_Flex_Hubspot, RootQueryToExpertConnection } from '~/graphql/types'
import { type ProfilePageLoaderData } from '~/services/hubspot.server'
import { Index as ConfettiShower } from 'confetti-react'

export const phases = [
  {
    label: '1. Sollicitatie',
    id: [217736412],
    step: 1
  },
  {
    label: '2. Gesprek',
    id: [217736413, 217736414],
    step: 2
  },
  {
    label: '3. Jouw matches',
    id: [217736415],
    step: 3
  },
  {
    label: '4. Baan gevonden!',
    id: [262707661],
    step: 4
  },
  {
    label: '5. Start onboarding',
    id: [217736416],
    step: 5
  }
]

type Props = Page_Flexcontent_Flex_Hubspot & {
  subdata?: {
    Advisors: RootQueryToExpertConnection
  }
}

export default function HubspotBanner({ fields }: { fields: Props }) {
  if (typeof document !== 'undefined') {
    const url = new URL(document.location.href)

    // set url in sessionStorage
    if (localStorage.getItem('profield_profile_url') !== url.href) {
      localStorage.setItem('profield_profile_url', url.href)
    }
  }

  const { hubspotData: profile } = useLoaderData<ProfilePageLoaderData>()
  const currentPhase = phases.find((phase) => phase.id.some((id) => String(id) === profile.deals[0].properties.dealstage))
  let matches = profile.deals.filter((deal) => currentPhase.id.some((id) => id === Number(deal.properties.dealstage)))

  if (currentPhase.step === 3) {
    matches = matches.filter((match) => !!match.company)
  }

  let advisorLastname = profile.owner.lastName
  const advisor = fields.subdata?.Advisors?.nodes.find((advisor) => {
    if (currentPhase.step === 2) {
      advisorLastname = profile.properties.carerix_first_meeting_owner_lastname
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_first_meeting_owner_firstname} ${profile.properties.carerix_first_meeting_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_first_meeting_owner_email?.toLowerCase()
      )
    }

    if (currentPhase.step === 4) {
      advisorLastname = profile.properties.carerix_job_owner_lastname
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_job_owner_firstname} ${profile.properties.carerix_job_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_job_owner_email?.toLowerCase()
      )
    }

    if (currentPhase.step === 5) {
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_job_owner_firstname} ${profile.properties.carerix_job_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_job_owner_email?.toLowerCase()
      )
    }

    return (
      advisor.title?.toLowerCase() === `${profile.owner.firstName} ${profile.owner.lastName}`.toLowerCase() ||
      advisor.recap.email?.toLowerCase() === profile.owner.email?.toLowerCase()
    )
  })

  if (typeof window !== 'undefined')
    return (
      <section data-component="HubspotBanner" className="bg-site-base pb-20 lg:pb-[250px] pt-[100px] lg:pt-[150px] -mt-[100px] relative">
        {currentPhase.step === 4 && (
          <ConfettiShower
            run={true}
            colors={['#78BF00', '#B5DC73', '#548600']}
            recycle={false}
            numberOfPieces={500}
            width={window.innerWidth}
            height={window.innerHeight}
          />
        )}

        <Animated delay={0}>
          <div className="absolute top-0 right-0 w-[642px]">
            <GreenGreenGrass className="w-full max-sm:opacity-30" />
          </div>
        </Animated>
        <div className="container">
          <div className="max-w-[1069px] mx-auto">
            <Animated delay={0}>
              <div className="text-xs w-fit border-2 border-white text-white rounded-full px-4 py-2">Pro Portal</div>
            </Animated>
            <Animated delay={100}>
              <h1 className="title--medium !leading-[1.2] text-white mt-8">
                {fields.title
                  .replace('{name}', profile.properties.firstname)
                  ?.split('|')
                  ?.map((t, i) => (
                    <Fragment key={i}>
                      <span key={i}>{t}</span>
                      <br className="last:hidden" />
                    </Fragment>
                  ))}
              </h1>
            </Animated>
            {fields.subtext && (
              <Animated delay={200}>
                <div>
                  <Content className="max-w-[580px] mt-10 children-p:text-xl children-p:text-site-accent">
                    {fields.subtext
                      ?.replace('{firstname}', advisor.recap.firstname)
                      ?.replace('{lastname}', advisorLastname)
                      ?.replace('{signing_name}', matches[0].properties.carerix_signing_location_name)
                      ?.replace('{signing_location}', matches[0].properties.carerix_signing_location)
                      ?.replace('{match_count}', matches.length === 1 ? '1 match' : `${matches.length} matches`)
                      ?.replace('{company_count}', matches.length === 1 ? 'dit bedrijf' : `deze bedrijven`)}
                  </Content>
                </div>
              </Animated>
            )}
            <Animated delay={150}>
              <div className="bg-white grid grid-cols-1 md:grid-cols-2 gap-y-7 p-8 sm:p-10 lg:p-16 mt-10 sm:mt-20 lg:mt-28">
                <Animated delay={300}>
                  <div>
                    <Content className="children-h2:text-3xl max-w-[340px] lg:children-h2:text-5xl children-h2:mb-2 children-p:font-bold children-p:text-base lg:children-p:text-xl">
                      {fields.description?.replace('{company_count}', matches.length === 1 ? 'dit bedrijf' : `deze bedrijven`)}
                    </Content>
                  </div>
                </Animated>
                <div className="flex flex-col gap-y-4 lg:gap-y-10">
                  {currentPhase.step === 3 && (
                    <>
                      {matches.map((match, index) => (
                        <Animated key={index} delay={500}>
                          <div
                            key={index}
                            style={{
                              animationDelay: `${index * 100 + 500}ms`
                            }}
                            className="flex gap-3 lg:gap-7"
                          >
                            <svg
                              className="min-w-6 mt-[2px]"
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 23 23"
                              fill="none"
                            >
                              <path d="M13.9491 0H9.05093V7.34722H13.9491V0Z" fill="#78BF00" />
                              <path d="M13.9491 15.6528H9.05093V23.0001H13.9491V15.6528Z" fill="#78BF00" />
                              <path d="M23 9.05103H15.6528V13.9492H23V9.05103Z" fill="#78BF00" />
                              <path d="M7.34722 9.05103H0V13.9492H7.34722V9.05103Z" fill="#78BF00" />
                            </svg>

                            <div className="content--light ">
                              <p>
                                {match.properties.carerix_first_meeting_location_name}
                                <br />
                                {match.properties.carerix_first_meeting_location}
                                <br />
                                <a href={`#${match.id}`}>&gt; Meer informatie</a>
                              </p>
                            </div>
                          </div>
                        </Animated>
                      ))}
                    </>
                  )}

                  {fields.items?.map((item, index) => (
                    <Animated key={index} delay={500}>
                      <div
                        key={index}
                        style={{
                          animationDelay: `${index * 100 + 500}ms`
                        }}
                        className="flex gap-3 lg:gap-7"
                      >
                        <svg
                          className="min-w-6 mt-[2px]"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 23 23"
                          fill="none"
                        >
                          <path d="M13.9491 0H9.05093V7.34722H13.9491V0Z" fill="#78BF00" />
                          <path d="M13.9491 15.6528H9.05093V23.0001H13.9491V15.6528Z" fill="#78BF00" />
                          <path d="M23 9.05103H15.6528V13.9492H23V9.05103Z" fill="#78BF00" />
                          <path d="M7.34722 9.05103H0V13.9492H7.34722V9.05103Z" fill="#78BF00" />
                        </svg>
                        <Content className="children-p:text-base children-strong:text-site-base lg:children-p:text-lg">
                          {item.text
                            .replace(
                              '{meeting_date}',
                              `<strong>${dayjs(profile.properties.carerix_first_meeting_date).format('DD MMMM YYYY')}</strong>`
                            )
                            .replace('{meeting_time}', dayjs(profile.properties.carerix_first_meeting_date).format('HH:mm'))
                            .replace(
                              '{meeting_address}',
                              {
                                'sollicitatiegesprek kantoor': profile.properties.carerix_first_meeting_location,
                                'sollicitatiegesprek op locatie': profile.properties.carerix_first_meeting_alternate_location_name,
                                'sollicitatiegesprek online': 'Het vindt online plaats'
                              }[profile.properties.carerix_first_meeting_type?.toLowerCase() || 'sollicitatiegesprek kantoor']
                            )
                            .replace('{signing_name}', matches[0].properties.carerix_signing_location_name)
                            .replace(
                              '{signing_date}',
                              dayjs(matches[0].properties.carerix_signing_date).format('DD MMMM YYYY [om] HH:mm [uur]')
                            )
                            .replace('{signing_location}', matches[0].properties.carerix_signing_location)
                            .replace('{firstday_name}', profile.properties.carerix_job_company_name)
                            .replace('{firstday_date}', dayjs(profile.properties.carerix_job_startdate).format('D MMMM YYYY'))
                            .replace('{firstday_time}', dayjs(profile.properties.carerix_job_startdate).format('HH:mm [uur]'))}
                        </Content>
                      </div>
                    </Animated>
                  ))}
                  {currentPhase.step === 2 && profile.properties.carerix_first_meeting_type === 'Sollicitatiegesprek Kantoor' && (
                    <Animated delay={500}>
                      <div className="max-sm:w-full max-md:mt-4 md:pl-8 lg:pl-[50px]">
                        <LosseLink
                          target="_blank"
                          to={`https://www.google.com/maps/dir//${profile.properties.carerix_first_meeting_location}`}
                          className="btn--conversion max-sm:w-full"
                        >
                          Plan je route in
                        </LosseLink>
                      </div>
                    </Animated>
                  )}
                </div>
              </div>
            </Animated>
          </div>
          <div className="mt-14 sm:mt-20 md:mt-28">
            <Animated delay={500}>
              <h2 className="text-right border-text--white title--semi-unit text-transparent mb-10">Voortgang</h2>
            </Animated>
            <div className="flex md:flex-row flex-col gap-y-3 items-center gap-x-3 lg:gap-x-5 relative justify-end">
              <Animated delay={150}>
                <div className="w-[60px] h-[90px] max-md:hidden border-2 border-white absolute -left-[80px] xl:left-[115px] -top-[120px]" />
              </Animated>
              <Animated delay={150}>
                <div className="w-[90px] h-[60px] max-md:hidden border-2 border-white absolute -left-[185px] xl:left-0 top-0" />
              </Animated>
              <Animated delay={150}>
                <div className="w-[60px] h-[90px] max-md:hidden border-2 border-white absolute -left-[80px] xl:left-[115px] -bottom-[120px]" />
              </Animated>

              {phases?.map((phase, index) => (
                <Animated key={phase.id[0]} delay={300}>
                  <div
                    key={phase.id[0]}
                    style={{
                      animationDelay: `${index * 100 + 300}ms`
                    }}
                    className={clsx(
                      currentPhase.step === phase.step ? 'border-site-accent text-white bg-site-accent' : 'border-white/40 text-white/40',
                      'border-2 w-full md:w-[200px] h-[60px] flex justify-center items-center'
                    )}
                  >
                    <span className="text-xl md:text-lg lg:text-lg">{phase.label}</span>
                  </div>
                </Animated>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
}
